/**
 * Stripe Checkout Links — single source of truth.
 *
 * Antes (audit 2026-06-14): los mismos 5 URLs de Stripe estaban duplicados
 * literal en `paymentLinks.ts` Y `telegramGroupsVideo.ts`. Si el operador
 * cambiaba un precio en Stripe y actualizaba SOLO uno de los dos archivos,
 * algunos visitantes veían el URL nuevo y otros el viejo — sin notificación.
 *
 * Ahora: una única export por URL, leída de env var con literal fallback
 * (los literals actuales se preservan para que el comportamiento sin env
 * vars sea idéntico al pre-fix). Cualquier override desde Vercel (sin
 * redeploy de código) aplica simultáneamente a todos los archivos que
 * importan.
 *
 * DataFast Method 3 (Payment Links): these buy.stripe.com URLs cannot receive
 * datafast_* metadata from our API. For channel attribution, each link in
 * Stripe Dashboard → After payment → Redirect must include:
 *   ?session_id={CHECKOUT_SESSION_ID}
 * e.g. https://www.delfuocampo.com/thank-you?session_id={CHECKOUT_SESSION_ID}
 * Prefer Method 1 (create-subscription-checkout) for VIP/MRR when possible.
 *
 * Política:
 *   - env var seteada y no vacía → ese URL gana
 *   - env var ausente/vacía     → literal fallback (= valor histórico)
 *
 * Env vars (todos opcionales; agregar a Vercel solo cuando el operador
 * quiera overridear sin redeploy):
 *   VITE_STRIPE_CHECKOUT_HIGH_PRICE_URL
 *   VITE_STRIPE_CHECKOUT_LOW_PRICE_URL
 *   VITE_STRIPE_CHECKOUT_AR_URL
 *   VITE_STRIPE_CHECKOUT_TALK_URL
 *   VITE_STRIPE_CHECKOUT_GROUP_TALK_URL
 *   VITE_STRIPE_CHECKOUT_MAINTENANCE_URL
 *   VITE_STRIPE_CHECKOUT_COMUNIDAD_URL
 */

function envOrFallback(envKey: string, fallback: string): string {
  const raw = (import.meta.env[envKey] as string | undefined)?.trim();
  return raw && raw.length > 0 ? raw : fallback;
}

/** Pricing alto: $29.99 (US, ES, DE, FR, MX, CO, PE, PY, BO, etc.) */
export const STRIPE_CHECKOUT_HIGH_PRICE_URL = envOrFallback(
  "VITE_STRIPE_CHECKOUT_HIGH_PRICE_URL",
  "https://buy.stripe.com/28E14n1RIbDje110RV5wI3x",
);

/** Pricing bajo: CL, BR, UY (precio reducido). */
export const STRIPE_CHECKOUT_LOW_PRICE_URL = envOrFallback(
  "VITE_STRIPE_CHECKOUT_LOW_PRICE_URL",
  "https://buy.stripe.com/8x29ATcwm7n35uvbwz5wI3w",
);

/** Argentina (fallback Stripe; el path normal es MercadoPago Bricks en /checkout-ar-channel-invite). */
export const STRIPE_CHECKOUT_AR_URL = envOrFallback(
  "VITE_STRIPE_CHECKOUT_AR_URL",
  "https://buy.stripe.com/fZueVd2VM8r79KL3035wI3u",
);

/** "Talk to me" — sesiones 1:1 individuales. */
export const STRIPE_CHECKOUT_TALK_URL = envOrFallback(
  "VITE_STRIPE_CHECKOUT_TALK_URL",
  "https://buy.stripe.com/7sYaEX9ka36N2ij1VZ5wI3q",
);

/** Group talk — sesiones en grupo. */
export const STRIPE_CHECKOUT_GROUP_TALK_URL = envOrFallback(
  "VITE_STRIPE_CHECKOUT_GROUP_TALK_URL",
  "https://buy.stripe.com/14A28raoe9vb5uvbwz5wI3r",
);

/** Maintenance fallback — usado por MaintenanceTemplate cuando la app está en modo mantenimiento. */
export const STRIPE_CHECKOUT_MAINTENANCE_URL = envOrFallback(
  "VITE_STRIPE_CHECKOUT_MAINTENANCE_URL",
  "https://buy.stripe.com/bJe3cv1RI7n39KL1VZ5wI3F",
);

/** Página /comunidad — redirección directa a Stripe. */
export const STRIPE_CHECKOUT_COMUNIDAD_URL = envOrFallback(
  "VITE_STRIPE_CHECKOUT_COMUNIDAD_URL",
  "https://buy.stripe.com/14A28rfIy6iZg998kn5wI3g",
);
